/**
 * App.tsx — top-level router setup.
 *
 * Route topology:
 *   AppLayout (bootstraps auth, shows "Loading…" until /me resolves)
 *   ├─ /login                — public (AuthCard)
 *   ├─ /signup               — public (AuthCard)
 *   ├─ RequireAuth + AppShell (sidebar + main outlet) — gated
 *   │   ├─ /                  — Home
 *   │   ├─ /team              — MemberRoster
 *   │   ├─ /team/add          — AddMemberWizard (gated: manage_members)
 *   │   ├─ /team/m/:memberId  — MemberDetail
 *   │   ├─ /team/m/:memberId/edit — EditForm (gated: manage_members)
 *   │   ├─ /jobs              — placeholder
 *   │   ├─ /library/departments       — Library hub (departments tab)
 *   │   ├─ /library/departments/new   — LibraryItemFormPage (create unit)
 *   │   ├─ /library/departments/:id   — LibraryItemFormPage (edit unit)
 *   │   ├─ /library/positions         — Library hub (positions tab)
 *   │   ├─ /library/member-profiles        — Library hub (member profiles tab)
 *   │   ├─ /library/member-profiles/:id    — StaffProfileDetailPage
 *   │   ├─ /library/job-profiles           — Library hub (job profiles)
 *   │   ├─ /library/job-profiles/new       — JobTemplateFormPage (create)
 *   │   ├─ /library/job-profiles/:id       — JobTemplateFormPage (edit or system view)
 *   │   ├─ /users             — placeholder (gated nav)
 *   │   ├─ /settings          — Settings
 *   │   ├─ /connectors        — ConnectorsHubPage
 *   │   └─ /connectors/:id    — ConnectorDetailPage
 *   └─ *                     — falls through to "/"
 */

import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { RequireAuth, useBootstrapAuth } from '@/features/auth';
import { AppShell } from '@/features/app-shell';
import LoginRoute from './routes/login';
import SignupRoute from './routes/signup';
import VerifyEmailSentRoute from './routes/verify-email-sent';
import VerifyEmailRoute from './routes/verify-email';
import WelcomeRoute from './routes/welcome';
import HomeRoute from './routes/home';
import TeamRoute from './routes/team';
import TeamAddRoute from './routes/team-add';
import TeamMemberRoute from './routes/team-member';
import TeamMemberEditRoute from './routes/team-member-edit';
import TeamMemberChatRoute from './routes/team-member-chat';
import { JobsPage, JobNewPage, JobDetailPage } from '@/features/jobs';
import { ActivityPage } from '@/features/activity';
import {
  JobTemplateFormPage,
  LibraryHubPage,
  LibraryItemFormPage,
  StaffProfileDetailPage,
} from '@/features/library';
import UsersRoute from './routes/users';
import SettingsRoute from './routes/settings';
import ConnectorsRoute from './routes/connectors';
import ConnectorsDetailRoute from './routes/connectors-detail';
import { t } from '@/lib/i18n';

function AppLayout() {
  const { isLoading } = useBootstrapAuth();
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-muted)',
        }}
        aria-live="polite"
      >
        {t('Loading…')}
      </div>
    );
  }
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    Component: AppLayout,
    children: [
      { path: '/login', Component: LoginRoute },
      { path: '/signup', Component: SignupRoute },
      { path: '/verify-email-sent', Component: VerifyEmailSentRoute },
      { path: '/verify-email', Component: VerifyEmailRoute },
      {
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { path: '/welcome', Component: WelcomeRoute },
          { path: '/', Component: HomeRoute },
          { path: '/team', Component: TeamRoute },
          { path: '/team/add', Component: TeamAddRoute },
          { path: '/team/m/:memberId', Component: TeamMemberRoute },
          { path: '/team/m/:memberId/edit', Component: TeamMemberEditRoute },
          { path: '/team/m/:memberId/chat', Component: TeamMemberChatRoute },
          { path: '/jobs', Component: JobsPage },
          { path: '/jobs/new', Component: JobNewPage },
          { path: '/jobs/:id', Component: JobDetailPage },
          { path: '/activity', Component: ActivityPage },
          { path: '/library', element: <Navigate to="/library/departments" replace /> },
          { path: '/library/departments', Component: LibraryHubPage },
          { path: '/library/departments/new', Component: LibraryItemFormPage },
          { path: '/library/departments/:id', Component: LibraryItemFormPage },
          { path: '/library/positions', Component: LibraryHubPage },
          { path: '/library/positions/new', Component: LibraryItemFormPage },
          { path: '/library/positions/:id', Component: LibraryItemFormPage },
          { path: '/library/member-profiles', Component: LibraryHubPage },
          { path: '/library/member-profiles/:id', Component: StaffProfileDetailPage },
          { path: '/library/job-profiles', Component: LibraryHubPage },
          { path: '/library/job-profiles/new', Component: JobTemplateFormPage },
          { path: '/library/job-profiles/:id', Component: JobTemplateFormPage },
          { path: '/users', Component: UsersRoute },
          { path: '/settings', Component: SettingsRoute },
          { path: '/connectors', Component: ConnectorsRoute },
          { path: '/connectors/:connectorId', Component: ConnectorsDetailRoute },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
